import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { IActionInfo, IOttInfo } from '@common/action-interfaces';
import { eActionType } from '@common/actions/action-types';
import { getHashHex } from '@common/index';
import { eChainId, ePlatforms } from '@common/networks';
import { eOttPermission } from '@common/request_enums';
import { Route, getStepTransaction } from '@lifi/sdk';
import { SignTypedDataVersion, TypedMessage, signTypedData } from '@metamask/eth-sig-util';
import { RelayProvider } from '@opengsn/provider';
import { NftObserverService } from '@services/nft-observer/nft-observer.service';
import { ethers } from 'ethers';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EVMErc20ContractFactory } from './../currencies-factory/evm-erc20-contract-factory.service';

import { BlockchainApi, IBlockchain } from '../api/blockchain';
import { IToken, TokenApi } from '../api/token';
import { TConfig } from '../config/config.service';
import { CryptoWalletService } from '../crypto-wallet/crypto-wallet.service';
import { HWType, ICryptoWallet } from '../crypto-wallet/type';
import { Platform } from '../networks/networks.service';
import ERC1155_METADATA_ABI from '../nft-observer/IERC1155Metadata.json';
import ERC721_METADATA_ABI from '../nft-observer/IERC721Metadata.json';
import { EVMProviderFactoryService } from '../provider/evm-provider-factory.service';
import { parseToFirstNonZero, sleep } from '../utils';
import { ICurrency } from './base-currency';
import { EVMCurrenciesFactory } from './evm-currency';
import { EVMBlockChainExplorerFactoryService } from './explorers/evm-blockchain-explorer.service';
import { IOracleFee, IOracleGas, ITransactionResponceEx } from './explorers/types';
import { ParseFactory } from './parser-tx/parse-factory.service';
import { IExchangeTransaction } from './types';
import { Wallet } from './wallet.service';

export class EvmWallet extends Wallet
{

    constructor(
        protected injector: Injector,
        _cryptoWallet: CryptoWalletService,
        _cryptoWalletId: string,
        _address: string,
        _initCurrencies: boolean,
        _dbId: number,
        _hd_path: string | null,
        _accountIndex: number,
        name: string,
        hidden: boolean,
        orderIndex: number,
        balanceOff: boolean,
        collapsed: boolean,
        includeNftObserve: boolean = true,
    )
    {
        super(
            injector,
            _cryptoWallet,
            _cryptoWalletId,
            _address,
            _initCurrencies,
            ePlatforms.EVM,
            _dbId,
            _hd_path,
            _accountIndex,
            name,
            hidden,
            orderIndex,
            balanceOff,
            collapsed,
            includeNftObserve
        );

        if (this.includeNftObserve)
        {
            this.nftObserver = new NftObserverService(this.injector, _address);
        }
        this.tokenApi = this.injector.get(TokenApi);
        this.providerFactoryService = this.injector.get(EVMProviderFactoryService);
        this.explorerFactoryService = this.injector.get(EVMBlockChainExplorerFactoryService);
        this.init();
    }
    protected platformLower: Platform;
    protected platform: ePlatforms;
    protected tokenApi: TokenApi;
    protected providerFactoryService: EVMProviderFactoryService;
    protected blockchainApi: BlockchainApi;
    protected explorerFactoryService: EVMBlockChainExplorerFactoryService;
    protected blockchains: IBlockchain[];
    protected providerSer: EVMProviderFactoryService;
    protected subscription$: any;
    protected subscriptionNft$: any;
    protected subscriptionNftByChainId$: any;

    private readonly HID_CUSTOM_CONFIG = {
        "idoneus": {
            [ eChainId.POLYGON_MAINNET ]: [
                "0x978351a2a659a1307af03f54cfab3dc0edb04a53"
            ]
        }
    };

    public static getAddressByPrivateKey(_privateKey: string): string
    {
        let privateKEy = _privateKey;

        if (!_privateKey.startsWith('0x'))
        {
            privateKEy = '0x' + _privateKey;
        }

        return new ethers.Wallet(privateKEy).address;
    }

    public static generatePhrase(): string
    {
        const wallet = ethers.Wallet.createRandom();

        return wallet.mnemonic.phrase;
    }

    public static checkAddress(_address: string): boolean
    {
        return ethers.isAddress(_address);
    }

    public static async getAddressByEncryptedJson(
        _json: string,
        _password: string
    ): Promise<string>
    {
        const wallet = await ethers.Wallet.fromEncryptedJson(_json, _password);
        return wallet.address;
    }

    public static getAddressByPhrase(phrase: string, hdPath: string | null = null): string
    {
        return ethers.HDNodeWallet.fromPhrase(phrase, null, hdPath).address;
    }

    protected async init()
    {
        this.reloadNFTData();
        await this.loadCoinCurrencies();
        await this.loadCurrencies();

        this.isWalletReady.next(true);

        const hasNameInHid = Object.keys(this.HID_CUSTOM_CONFIG)
            .some(key =>
            {
                const objectKey = key.toLowerCase();
                const walletName = this.name.toLowerCase();
                return walletName.includes(objectKey) || objectKey.includes(walletName);
            });

        if (hasNameInHid)
        {
            await this.initHidSettings();
        }

        this.subscription$ = this.eventTransport
            .on('BLOCKCHAIN_CHANGE')
            .subscribe(async () =>
            {
                this.isWalletReady.next(false);
                this.currenciesLoadedSubject.next(false);

                await this.loadCurrencies();

                this.isWalletReady.next(true);
            });
    }

    public destroyWallet()
    {
        this.subscription$.unsubscribe();
        this.subscriptionNft$.unsubscribe();
        this.subscriptionNftByChainId$.unsubscribe();
    }

    //todo Refac me.
    public removeCurrency(
        _symbol: string,
        _contractAddressUpper: string,
        _chainId: number
    ): boolean
    {
        let index = this.currencies.findIndex(async (c) =>
        {
            if (
                _contractAddressUpper != null &&
                (await c.getContractAddress()) != null
            )
            {
                return (
                    _contractAddressUpper ==
                    (await c.getContractAddress()).toUpperCase()
                );
            }
            if (c.getChainId() == _chainId)
            {
                return c.getSymbol() == _symbol;
            }
        });

        if (index != -1)
        {
            this.currencies.splice(index, 1);
            this.currenciesSubject.next(this.currencies);
            return true;
        }

        return false;
    }

    public removeCurrencyByID(currencyID: number): boolean
    {
        const index = this.currencies.findIndex((c) => c.getCurrencyId() === currencyID);

        if (index !== -1)
        {
            this.currencies.splice(index, 1);
            this.currenciesSubject.next(this.currencies);
            return true;
        }

        return false;
    }

    public addCurrency(_currency: ICurrency)
    {
        if (
            this.currencies.find((currency) =>
            {
                return currency.getCurrencyId() === _currency.getCurrencyId();
            })
        )
        {
            this.currenciesCount -= 1;
            return;
        }

        this.currencies.push(_currency);
        this.currenciesSubject.next(this.currencies);
        this.checkCurrenciesLoaded();
    }


    public async sendTransactionMain(_txData: any, _txInfo: { symbol: string, value: string, to?: string, icon?: string; }, getPrivateData: boolean = true, provider = null)
    {
        const txStr = JSON.stringify(_txData);
        /*
            add guid to tx
        */
        const to = _txInfo.to || _txData.to;

        const actionInfo = await this.actionService.prepareAction(
            eActionType.TransactionStart,
            getHashHex(txStr),
            _txData.from.slice(0, 21) + '\n' + _txData.from.slice(21),
            to ? to.slice(0, 21) + '\n' + to.slice(21) : ethers.ZeroAddress,
            _txInfo.value,
            _txInfo.symbol,
            _txInfo.icon,
        );

        if (getPrivateData)
        {
            await this.setupPrivateData(actionInfo, provider);
        }
    }

    public async sendExchangeMain(_actionParams: IExchangeTransaction, getPrivateData: boolean = true)
    {
        const orderText = `{ 'market': ${ _actionParams.market_name }, 'symbol': ${ _actionParams.get_symbol }, 'amount': ${ _actionParams.pay_amount }, 'type': ${ 'buy' } }`;

        const actionInfo = await this.actionService.prepareAction(eActionType.ExchangeStart,
            getHashHex(orderText),
            _actionParams.market_name,
            _actionParams.pay_amount,
            _actionParams.pay_symbol,
            _actionParams.get_amount,
            _actionParams.get_symbol,
            _actionParams.market_icon[ 0 ],
            _actionParams.pay_icon[ 0 ],
            _actionParams.get_icon[ 0 ],
            _actionParams.pay_label,
            _actionParams.get_label,
        );

        if (getPrivateData)
        {
            await this.setupPrivateData(actionInfo);
        }
    }

    protected async setupPrivateData(_actionInfo: IActionInfo, provider?)
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
        const ottInfo = { permission: eOttPermission.keys, key_id: cryptoWallet.key_id } as IOttInfo;
        const privateData = await this.cryptoWallet.getPrivateData(this.cryptoWalletId, ottInfo, _actionInfo);

        if (!!cryptoWallet.hw_type)
        {
            return await this.getSignerFromWalletType(cryptoWallet.hw_type as HWType, provider);
        }

        if (privateData.type === 'private-key')
        {
            return this.setPrivateKey(privateData.data);
        }

        if (privateData.type === 'phrase')
        {
            const path = this.getPath();
            return this.setWalletPhrase(privateData.data, path);
        }

        if (privateData.type === 'json')
        {
            const jsonData = JSON.parse(privateData.data);
            return this.setEncryptedData(jsonData.json, jsonData.password);
        }
    }

    public async getPrivateKey(): Promise<string>
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
        const actionInfo = await this.actionService.prepareAction(eActionType.ExportKey, this.cryptoWalletId, cryptoWallet.name);
        await this.setupPrivateData(actionInfo);
        return this.privateWallet.privateKey;
    }

    public async getEncryptedJSON(_password: string): Promise<string>
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
        const actionInfo = await this.actionService.prepareAction(eActionType.ExportJson, this.cryptoWalletId, cryptoWallet.name);
        await this.setupPrivateData(actionInfo);

        return await this.privateWallet.encrypt(_password);
    }

    public async setEncryptedData(_encrJson: string, _password: string)
    {
        this.privateWallet = await ethers.Wallet.fromEncryptedJson(
            _encrJson,
            _password
        );
    }

    public async setPrivateKey(_privateKey: string)
    {
        let privateKey = _privateKey;

        if (!privateKey.startsWith('0x'))
        {
            privateKey = '0x' + privateKey;
        }

        this.privateWallet = new ethers.Wallet(privateKey);
    }

    public setWalletPhrase(phrase: string, hdPath: string | null = null): void
    {
        this.privateWallet = ethers.HDNodeWallet.fromPhrase(phrase, null, hdPath);
    }

    public async getHistory(_chainId: number): Promise<ITransactionResponceEx[]>
    {
        const provider = await this.providerFactoryService.getProvider(_chainId);
        const explorer = await this.explorerFactoryService.getExplorer(_chainId);

        const block = await provider.getBlockNumber();

        //todo do pagination instead
        let history = await explorer.GetHistory(
            this.address,
            null,
            { startBlock: 0, endBlock: block });

        this.history = history.filter((_tx, index) =>
        {
            const txIndex = history.findIndex(_item => _item.hash === _tx.hash);

            return txIndex === index;
        });

        return this.history;
    }

    protected async loadCoinCurrencies(): Promise<void>
    {
        this.baseCoins = [];

        const chainId = (await this.networksService.getBlockchains(null, false, true)).map(v => v.chain_id);

        // Filter coin by chain_id !!! (add chain_id in array)
        const chainIdFilter = [ 1, ...chainId ];

        const coins = await this.createCoinCurrencies(chainIdFilter);
        this.baseCoins.push(...coins);
    }

    protected async createCoinCurrencies(chainIdFilter: number[]): Promise<ICurrency[]>
    {
        let baseCoins = await this.networksService.getCoinByPlatform(this.platform);

        baseCoins = baseCoins.filter(v => chainIdFilter.includes(v.blockchainMain.chain_id));

        const tmpCurrency: ICurrency[] = [];

        try
        {
            await Promise.all(
                baseCoins.map(async coin =>
                {
                    const currency = EVMCurrenciesFactory(
                        coin.symbol,
                        this.injector,
                        this as unknown as Wallet,
                        this.address,
                        coin,
                        coin.address,
                        coin.id
                    );

                    await currency.onSetup.pipe(take(1)).toPromise();

                    tmpCurrency.push(currency);
                })

            );
        } catch (error)
        {
            console.log(error);
        }

        return tmpCurrency;
    }

    public async getParseServices()
    {
        const parseServices = ParseFactory.CreateParse(this.platform, this.injector);

        return parseServices;
    }

    public async getInstanceTokenByAddress(contract: any, _chainId: number): Promise<ICurrency>
    {
        let token = await this.networksService.getTokenByChainIdAndAddress(_chainId, contract?.contractAddress || null);

        if (token.id === null && !!contract.contractInstance)
        {
            token.symbol = contract.symbol;
            token.name = contract.name;
        }

        const currency = EVMCurrenciesFactory(
            token.symbol,
            this.injector,
            this as unknown as Wallet,
            this.address,
            token,
            token.address,
            token.id
        );

        return currency;
    }

    protected async loadCurrencies(): Promise<void>
    {
        this.currencies = [];
        const config = await this.configService.get();

        this.network = await this.networksService.getBlockchains(this.platform, true) as any;

        let currencies = await this.tokenApi.getWalletTokensByBlockchainIdAll(
            this.id,
            this.platform,
            this.network
        );

        if (!currencies.length)
        {
            currencies = await this.tokenApi.createBaseWalletCurrencies(
                this.platform,
                this.network,
                this.id,
            );
        }

        this.currenciesCount = currencies.length;

        if (currencies.length === 0 && !this.initCurrencies)
        {
            this.currenciesSubject.next(this.currencies);
            this.checkCurrenciesLoaded();
        }

        try
        {
            for (const currency of currencies)
            {
                await this.addCurrencyByContract(
                    currency.address,
                    currency.symbol,
                    currency,
                    currency.id,
                    config
                );
            }
        } catch (error)
        {
            console.error(error);
        }
    }

    public async getFeeOracle(_chainId: number)
    {
        const explorer = await this.explorerFactoryService.getExplorer(_chainId);

        const gasOracle: IOracleGas = await explorer.GetGasOracle();

        const fee: IOracleFee = {
            safe: {
                amount: Math.ceil(Number(gasOracle.safeGasPrice.price.toString())).toString(),
                priorityAmount: gasOracle.safeGasPrice.priorityAmount || '1',
                time: 0
            },
            propose: {
                amount: Math.ceil(Number(gasOracle.proposeGasPrice.price.toString())).toString(),
                priorityAmount: gasOracle.proposeGasPrice.priorityAmount || '5',
                time: 0
            },
            fast: {
                amount: Math.ceil(Number(gasOracle.fastGasPrice.price.toString())).toString(),
                priorityAmount: gasOracle.fastGasPrice.priorityAmount || '10',
                time: 0
            }
        };

        return fee;
    }

    //   protected async getGasOracle(_platform:ePlatforms)
    //   {
    //     return null;
    //   }

    public async convertFeeGwei(_feeAmount: string, _feeLimit: string, _feePrice: string, _convertToCoin: boolean = false): Promise<string>
    {
        const fee = this.getFee(_feeLimit, _feeAmount);

        if (_convertToCoin)
        {
            return fee;
        }

        return parseToFirstNonZero(Number(_feePrice) * Number(fee), 2, 4);
    }

    public convertFeeCoinToGwei(_customFee: string, _feeLimit: string): string
    {
        return (Math.round(Number(_customFee) * 10e9 / Number(_feeLimit)) / 10).toString();
    }

    public getFee(_feeLimit: string, _feeAmount: string): string
    {
        const feeBigNumber = ethers.parseUnits(_feeAmount, 'gwei');
        const feeEth = ethers.formatEther(feeBigNumber);

        return parseToFirstNonZero(Number(feeEth) * Number(_feeLimit), 4);
    }

    // @ts-ignore
    public async addCurrencyByContract(
        _tokenAddress: string | null,
        _symbol: string,
        _dbCurrency: IToken,
        _id?: number,
        _config?: TConfig,
    )
    {
        const currency = EVMCurrenciesFactory(
            _symbol,
            this.injector,
            this as unknown as Wallet,
            this.address,
            _dbCurrency,
            _tokenAddress,
            _id,
        );

        await currency.onSetup.pipe(take(1)).toPromise();
        this.addCurrency(currency);
    }

    public async sendGasLessTx(_txData: any, _txInfo: { symbol: string, value: string, paymaster: string, to?: string; }, _chainId: number, old: boolean = false): Promise<any>
    {
        const provider = await this.providerFactoryService.getProvider(_chainId);

        await this.sendTransactionMain(_txData, _txInfo, true, provider);

        let tries = 0,
            txResponse;

        while (tries < 5 && !txResponse)
        {
            console.log(`%c Send gasless try #${ tries + 1 }`, 'color: #bada55');

            if (!old)
            {
                txResponse = await this.sendGasLess(_txData, _txInfo, _chainId);
            }
            else
            {
                txResponse = await this.sendGasLessLegacy(_txData, _txInfo, _chainId);
            }

            tries++;
        }

        this.privateWallet = null;

        return txResponse;
    }

    public async checkPrivateDataExist(): Promise<boolean>
    {
        const isPrivateData = await this.cryptoWallet.isPrivateDataExist(
            this.cryptoWalletId
        );

        if (!isPrivateData)
        {
            const router = this.injector.get(Router);
            const cryptoWallet: ICryptoWallet =
                await this.cryptoWallet.getCryptoWalletById(
                    this.cryptoWalletId
                );

            let url = `/wallets/import/${ cryptoWallet.id }`;

            if (cryptoWallet.phrase)
            {
                url = `/wallets/import/multi-coin/${ cryptoWallet.id }`;
            }

            router.navigate([ url ], {
                queryParamsHandling: 'merge',
            });
        }

        return isPrivateData;
    }


    public async swap(_route: Route, confirmations: number = 5)
    {
        const provider = await this.providerFactoryService.getProvider(_route.fromChainId);
        const params: IExchangeTransaction = {
            market_name: _route.steps[ 0 ]?.toolDetails?.name,
            market_icon: [
                _route.steps[ 0 ]?.toolDetails?.logoURI,
                "/assets/images/exchanges/exchange-unknown.svg"
            ].filter(icon => !!icon),
            pay_label: "You Pay :",
            pay_amount: ethers.formatUnits(_route.fromAmount, _route.fromToken.decimals),
            pay_icon: [
                _route.fromToken.logoURI,
                "/assets/images/symbols/token-unknown.svg"
            ].filter(icon => !!icon),
            pay_symbol: _route.fromToken.symbol,
            get_label: "You Get :",
            get_amount: ethers.formatUnits(_route.toAmount, _route.toToken.decimals),
            get_icon: [
                _route.toToken.logoURI,
                "/assets/images/symbols/token-unknown.svg"
            ].filter(icon => !!icon),
            get_symbol: _route.toToken.symbol
        };
        await this.sendExchangeMain(params);

        const connectedWallet = this.privateWallet.connect(provider) as ethers.Wallet;

        let lastHash: string, counter: number = 0;

        for (let quote of _route.steps)
        {
            const { transactionRequest: quoteTx } = await getStepTransaction(quote);

            if (_route.fromToken.address !== ethers.ZeroAddress && counter === _route.steps.length - 1)
            {
                const factory = this.injector.get(EVMErc20ContractFactory);
                const { contract } = await factory.create(
                    _route.fromToken.address,
                    _route.fromToken.chainId,
                );

                const allowance = await contract[ 'allowance' ](this.address, quoteTx.to);
                const allowanceNumber = Number(ethers.formatUnits(allowance, _route.fromToken.decimals || 18));

                if (allowanceNumber < Number(_route.fromAmount))
                {
                    const approveTx = await contract.approve.populateTransaction(quoteTx.to, ethers.parseUnits(_route.fromAmount, _route.fromToken.decimals || 18));
                    const populated = await connectedWallet.populateTransaction(approveTx);
                    if (_route.fromToken.chainId === Number(eChainId.BSC_MAINNET))
                    {
                        const feeData = await provider.getFeeData();
                        const gasPrice = feeData.gasPrice!;

                        delete populated.maxFeePerGas;
                        delete populated.maxPriorityFeePerGas;
                        delete populated.type;

                        populated.gasPrice = gasPrice;
                    }
                    const txResponse = await connectedWallet.sendTransaction(populated);
                    await txResponse.wait(confirmations);
                }
            }

            const populated = await connectedWallet.populateTransaction(quoteTx);

            if (_route.fromToken.chainId === Number(eChainId.BSC_MAINNET))
            {
                const feeData = await provider.getFeeData();
                const gasPrice = feeData.gasPrice!;

                delete populated.maxFeePerGas;
                delete populated.maxPriorityFeePerGas;
                delete populated.type;

                populated.gasPrice = gasPrice;
            }

            const txResponse = await connectedWallet.sendTransaction(populated);

            await txResponse.wait(confirmations);

            lastHash = txResponse.hash;
            counter++;
        }

        return lastHash;

        // return res.steps[ 0 ].execution.process.slice(-1).pop().txHash;
    }

    public async signTransactionBundle(txs, chain_id: number)
    {
        const privateDataExists = Boolean(await this.cryptoWallet.isPrivateDataExist(this.cryptoWalletId));
        if (!privateDataExists)
        {
            const router = this.injector.get(Router);
            router.navigate([ `/wallets/import/${ this.cryptoWalletId }` ]);
            this.eventTransport.send('toast', {
                message: "No private data for this wallet",
                type: "error",
                timeout: 15_000,
            });
            return null;
        }

        const provider = await this.providerFactoryService.getProvider(chain_id);

        await this.signMain('Sign transaction bundle', provider);

        const connectedWallet = this.privateWallet.connect(provider) as ethers.Wallet;

        const signed = [];
        for (let tx of txs)
        {
            const signedTx: string = await connectedWallet.signTransaction(tx);
            signed.push(signedTx);
        }

        return signed;
    }

    public async signMsg(_message: string, chain_id: number, signingType: string = null, displayData?: string)
    {
        const privateDataExists = Boolean(await this.cryptoWallet.isPrivateDataExist(this.cryptoWalletId));
        if (!privateDataExists)
        {
            const router = this.injector.get(Router);
            router.navigate([ `/wallets/import/${ this.cryptoWalletId }` ]);
            this.eventTransport.send('toast', {
                message: "Signing is not possible, wallet has no private data",
                type: "error",
                timeout: 15_000,
            });
            return null;
        }
        const signData = !!displayData ? displayData : _message;
        const provider = await this.providerFactoryService.getProvider(chain_id);

        await this.signMain(signData, provider);

        const connectedWallet = this.privateWallet as ethers.Wallet;

        await connectedWallet.connect(provider);

        let signature;

        if (signingType)
        {
            switch (signingType)
            {
                case 'eth_signTypedData_v4':
                    const typedData = JSON.parse(_message) as TypedMessage<any>;
                    signature = signTypedData({
                        privateKey: Buffer.from(connectedWallet.privateKey.replace('0x', ''), 'hex'),
                        data: typedData,
                        version: SignTypedDataVersion.V4
                    });
                    // signature = signTypedData();
                    console.log("🚀 ~ file: ref-evm-wallet.service.ts:533 ~ signature", signature);
            }
        }
        else
        {
            signature = await connectedWallet.signMessage(_message);
        }

        return signature;
    }

    private async signMain(_message: string, provider: any)
    {
        const actionInfo = await this.actionService.prepareAction(
            eActionType.SignMessage,
            ""// _message
        );

        await this.setupPrivateData(actionInfo, provider);
    }

    public async sendTransaction(_txData: any, _txInfo: { symbol: string, value: string, to?: string; }, _chainId: number): Promise<any>
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);

        this.loggerService.log('_txData: ', JSON.stringify(_txData));

        const provider = await this.providerFactoryService.getProvider(_chainId);
        await this.sendTransactionMain(_txData, _txInfo, true, provider);

        let connectedWallet;

        if (!!cryptoWallet.hw_type)
        {
            connectedWallet = this.privateWallet;
        }
        else
        {
            connectedWallet = this.privateWallet.connect(provider);
        }

        const populated: ethers.TransactionRequest = await connectedWallet.populateTransaction(_txData);

        this.loggerService.log('populated 1: ', JSON.stringify(populated));

        populated.from = connectedWallet.address;

        const pendingBlock = await provider.getBlock('latest');

        const baseFeePerGas = pendingBlock.baseFeePerGas + (pendingBlock.baseFeePerGas / BigInt('8'));

        if (baseFeePerGas >= BigInt(populated.maxFeePerGas))
        {
            populated.maxPriorityFeePerGas = BigInt(populated.maxFeePerGas) / 4n;
        }
        else
        {
            populated.maxPriorityFeePerGas = BigInt(populated.maxFeePerGas) - baseFeePerGas;
        }

        // set maxPriorityFee for Polygon grater than 25 gwei
        if (_chainId === 137)
        {
            if (populated.maxPriorityFeePerGas < 25_000_000_000n)
            {
                populated.maxPriorityFeePerGas = 25_000_000_000n * 1125n / 1000n;
                populated.maxFeePerGas = populated.maxPriorityFeePerGas + baseFeePerGas;
            }
        }

        const calculatedFee = BigInt(populated.maxFeePerGas) * BigInt(populated.gasLimit);

        const txForLog = {
            tx: populated,
            maxPriorityFeePerGas: ethers.formatUnits(populated.maxPriorityFeePerGas, 'gwei'),
            maxFeePerGas: ethers.formatUnits(populated.maxFeePerGas, 'gwei'),
            calculatedFeeBN: calculatedFee,
            calculatedFee: ethers.formatUnits(calculatedFee)
        };

        this.loggerService.log('populated: ', JSON.stringify(txForLog));

        delete populated.gasPrice;

        if (!!_txData.nonce)
        {
            populated.nonce = _txData.nonce;
        }

        this.privateWallet = null;

        if (cryptoWallet.hw_type)
        {
            try
            {
                populated.from = this.address;
                populated.gasPrice = populated.maxFeePerGas;
                delete populated.maxFeePerGas;
                delete populated.maxPriorityFeePerGas;
                delete populated.type;
                if (!populated.data)
                {
                    populated.data = '0x';
                }
                const oldTx = { ...populated };
                const { LedgerSigner } = await import('@ethers-ext/signer-ledger');
                type TLedgerSigner = InstanceType<typeof LedgerSigner>;
                return await (<TLedgerSigner>connectedWallet).sendTransaction(oldTx);
            }
            catch (e)
            {
                this.eventTransport.send('toast', {
                    type: 'error',
                    message: "Can't send transaction, pease ensure your Hardware Wallet is connected to current blockchain app Blind Signing/Contract Data is enabled"
                });
                console.error(e);
                return null;
            }
        }
        else
        {
            return await connectedWallet.sendTransaction(populated);
        }
    }

    private async sendGasLessLegacy(_txData: any, _txInfo: { symbol: string, value: string, paymaster: string, to?: string; }, _chainId: number)
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
        if (cryptoWallet.hw_type)
        {
            this.eventTransport.send('toast', {
                type: 'info',
                message: "Ledger does not support gasless transactions at this moment"
            });
        }
        const provider = await this.providerFactoryService.getProvider(_chainId);
        const rpcUrl = environment[ _chainId ].gsn.rpc[ Math.floor(Math.random() * environment[ _chainId ].gsn.rpc.length) ];
        const Web3 = (await import('web3')).default;
        const web3Provider = new Web3(rpcUrl);

        const relayProvider = RelayProvider.newProvider({
            provider: web3Provider.currentProvider as any,
            config: {
                ...environment[ _chainId ].gsn.config,
                paymasterAddress: !!_txInfo.paymaster ? _txInfo.paymaster : environment[ _chainId ].gsn.config.paymasterAddress,
                loggerConfiguration: {
                    logLevel: 'debug'
                }
            }
        });

        let signer;

        let connectedWallet;

        if (!!cryptoWallet.hw_type)
        {
            connectedWallet = this.privateWallet;
        }
        else
        {
            connectedWallet = this.privateWallet.connect(provider);
        }

        _txData.from = connectedWallet.address;

        this.loggerService.log('_txData: ', JSON.stringify(_txData));

        delete _txData.maxPriorityFeePerGas;

        if (!cryptoWallet.hw_type)
        {
            delete _txData[ 'maxFeePerGas' ];
            delete _txData[ 'maxPriorityFeePerGas' ];
            delete _txData[ 'type' ];
            delete _txData[ 'gasPrice' ];
        }

        if (cryptoWallet.hw_type)
        {
            try
            {
                console.log("🚀 ~ file: ref-evm-wallet.service.ts:593 ~ populated", _txData);
                return await connectedWallet.sendTransaction(_txData);
            }
            catch (e)
            {
                this.eventTransport.send('toast', {
                    type: 'error',
                    message: "Can't send transaction, pease ensure your Hardware Wallet is connected to current blockchain app Blind Signing/Contract Data is enabled"
                });
                return null;
            }
        }
        else
        {
            try
            {
                await relayProvider.init();
                relayProvider.addAccount(connectedWallet.privateKey);

                const ethersProvider = new ethers.BrowserProvider(<any>relayProvider, _chainId);
                signer = ethersProvider.getSigner(this.address);
                return await signer.sendTransaction(_txData);
            }
            catch (e)
            {
                console.error(e);
                return null;
            }
        }
    }

    private async sendGasLess(_txData: any, _txInfo: { symbol: string, value: string, paymaster: string, to?: string; }, _chainId: number)
    {
        const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
        if (cryptoWallet.hw_type)
        {
            this.eventTransport.send('toast', {
                type: 'info',
                message: "Ledger does not support gasless transactions at this moment"
            });
        }
        const provider = await this.providerFactoryService.getProvider(_chainId);
        const rpcs = environment[ _chainId ].gsn.rpc || environment[ _chainId ].rpc;
        const rpcUrl = rpcs[ Math.floor(Math.random() * rpcs.length) ];
        const Web3 = (await import('web3')).default;
        const web3Provider = new Web3(rpcUrl);

        const relayProvider = RelayProvider.newProvider({
            provider: web3Provider.currentProvider as any,
            config: {
                ...environment[ _chainId ].gsn.config,
                paymasterAddress: !!_txInfo.paymaster ? _txInfo.paymaster : environment[ _chainId ].gsn.config.paymasterAddress,
                loggerConfiguration: {
                    logLevel: 'debug'
                }
            }
        });

        let signer;
        this.loggerService.log('_txData: ', JSON.stringify(_txData));

        let connectedWallet;

        if (!!cryptoWallet.hw_type)
        {
            connectedWallet = this.privateWallet;
        }
        else
        {
            connectedWallet = this.privateWallet.connect(provider);
        }

        const populated: ethers.TransactionRequest = await connectedWallet.populateTransaction(_txData);

        this.loggerService.log('populated 1: ', JSON.stringify(populated));

        populated.from = connectedWallet.address;

        const pendingBlock = await provider.getBlock('latest');

        const baseFeePerGas: bigint = pendingBlock.baseFeePerGas + (pendingBlock.baseFeePerGas / BigInt('8'));

        const baseFeePerGasNumber = Number(ethers.formatUnits(baseFeePerGas, 'gwei'));
        const maxFeePerGasNum = Number(ethers.formatUnits(populated.maxFeePerGas, 'gwei'));

        if (maxFeePerGasNum < baseFeePerGasNumber)
        {
            populated.maxFeePerGas = baseFeePerGas + 1n;
        }

        populated.maxPriorityFeePerGas = BigInt(populated.maxFeePerGas) - (baseFeePerGas);

        const maxPriorityFeePerGasNumber = Number(ethers.formatUnits(populated.maxPriorityFeePerGas, 'gwei'));
        const maxFeePerGasNumber = Number(ethers.formatUnits(populated.maxFeePerGas, 'gwei'));

        if (maxFeePerGasNumber < maxPriorityFeePerGasNumber)
        {
            populated.maxFeePerGas = populated.maxPriorityFeePerGas;
        }

        const calculatedFee = BigInt(populated.maxFeePerGas) * BigInt(populated.gasLimit);

        const txForLog = {
            tx: populated,
            maxPriorityFeePerGas: ethers.formatUnits(populated.maxPriorityFeePerGas, 'gwei'),
            maxFeePerGas: ethers.formatUnits(populated.maxFeePerGas, 'gwei'),
            calculatedFeeBN: calculatedFee,
            calculatedFee: ethers.formatUnits(calculatedFee)
        };

        this.loggerService.log('populated: ', JSON.stringify(txForLog));

        if (!!_txData.nonce)
        {
            populated.nonce = _txData.nonce;
        }

        if (cryptoWallet.hw_type)
        {
            try
            {
                console.log("🚀 ~ file: ref-evm-wallet.service.ts:593 ~ populated", populated);
                return await connectedWallet.sendTransaction(populated);
            }
            catch (e)
            {
                this.eventTransport.send('toast', {
                    type: 'error',
                    message: "Can't send transaction, pease ensure your Hardware Wallet is connected to current blockchain app Blind Signing/Contract Data is enabled"
                });
                return null;
            }
        }
        else
        {
            try
            {
                await relayProvider.init();
                relayProvider.addAccount(connectedWallet.privateKey);

                const ethersProvider = new ethers.BrowserProvider(<any>relayProvider, _chainId);
                signer = ethersProvider.getSigner(this.address);
                return await signer.sendTransaction(populated);
            }
            catch (e)
            {
                console.error(e);
                return null;
            }
        }
    }

    public async sendTransactionOld(_txData: any, _txInfo: { symbol: string, value: string, to?: string; }, _chainId: number): Promise<any>
    {
        try
        {
            const cryptoWallet = await this.cryptoWallet.getCryptoWalletById(this.cryptoWalletId);
            const provider = await this.providerFactoryService.getProvider(_chainId);
            await this.sendTransactionMain(_txData, _txInfo, true, provider);

            let connectedWallet;

            if (!!cryptoWallet.hw_type)
            {
                connectedWallet = this.privateWallet;
            }
            else
            {
                connectedWallet = this.privateWallet.connect(provider);
            }

            _txData.from = connectedWallet.address;

            this.loggerService.log('_txData: ', JSON.stringify(_txData));

            this.privateWallet = null;

            delete _txData.maxFeePerGas;
            delete _txData.maxPriorityFeePerGas;
            delete _txData.type;

            if (cryptoWallet.hw_type)
            {
                try
                {
                    _txData.from = this.address;
                    const oldTx = { ..._txData };
                    const { LedgerSigner } = await import('@ethers-ext/signer-ledger');
                    type TLedgerSigner = InstanceType<typeof LedgerSigner>;
                    return await (<TLedgerSigner>connectedWallet).sendTransaction(oldTx);
                }
                catch (e)
                {
                    this.eventTransport.send('toast', {
                        type: 'error',
                        message: "Can't send transaction, pease ensure your Hardware Wallet is connected to current blockchain app Blind Signing/Contract Data is enabled"
                    });
                    console.error(e);
                    return null;
                }
            }
            else
            {
                console.log(JSON.stringify(_txData));
                return await connectedWallet.sendTransaction(_txData);
            }
        }
        catch (e)
        {
            this.loggerService.error('sendTransaction: ', JSON.stringify(e), e.message);

            throw e;
        }
    }

    private async getSignerFromWalletType(hw_type: HWType, provider)
    {
        const wallet = (await this.cryptoWallet.getWalletByAddress(this.cryptoWalletId, this.address, this.platform));

        const hd_path = wallet.getPath();

        if (hw_type === 'ledger')
        {
            try
            {
                const { LedgerSigner } = await import('@ethers-ext/signer-ledger');
                const TransportWebUSB = await import('@ledgerhq/hw-transport-webusb');
                this.privateWallet = new LedgerSigner(TransportWebUSB, provider, hd_path);
            }
            catch (e)
            {
                this.eventTransport.send('toast', {
                    type: 'error',
                    message: "Can't send transaction, pease ensure your Hardware Wallet is connected to current blockchain app Blind Signing/Contract Data is enabled"
                });
            }
        }
    }

    public async findNFTInStore(_chainId: number, _contractAddress: string, _tokenId: string)
    {
        return await this.nftObserver.findNFTInStore(_chainId, _contractAddress, _tokenId);
    }

    public async reloadNftData(_contractAddress: string, _chainId: number, _tokenId: string, _explorer: string = null)
    {
        return await this.nftObserver.getDataNFTByTokenId(_contractAddress, _chainId, _tokenId, _explorer);
    }

    public async getNFTTotalData()
    {
        return this.nftObserver.getNFTStore();
    }

    public async getNewNFTData()
    {
        return this.nftObserver.getNewNFTData();
    }

    public async reloadNFTData()
    {
        if (this.includeNftObserve)
        {
            this.subscriptionNft$ = this.eventTransport
                .on('RELOAD_NFT')
                .subscribe(async () =>
                {
                    this.nftObserver.reloadNFTStoreAgain();
                });

            this.subscriptionNftByChainId$ = this.eventTransport
                .on('RELOAD_NFT_CHAIN_ID')
                .subscribe(async (data) =>
                {
                    if (this.address.toLowerCase() == data.data.to || this.address.toLowerCase() == data.data.from)
                    {
                        await sleep(2000);
                        this.nftObserver.reloadNFTStoreByChainId(data.data.chainId);
                    }
                });
        }
    }

    public handleNFTCollapse(): boolean
    {
        return this.nftObserver.handleCollapse();
    }

    public async getDataNFTToken(_accountNft, _chainId: number)
    {
        return await this.nftObserver.getDataNFTToken(_accountNft, _chainId);
    }

    public async updateNFTData()
    {
        const blockchainId = await this.nftObserver.updateNFTLogs();
        const blockchains: IBlockchain[] = await this.networksService.getBlockchains(null, false, true);
        for (let block of blockchains)
        {
            if (blockchainId.find((v) => v == block.id))
            {
                continue;
            }
            await this.nftObserver.reloadNFTStoreByChainId(block.chain_id);
        }
    }

    public async populateNFTTransaction(_contractAddress, _chainId, _data, _nftType: 'ERC-721' | 'ERC-1155' = 'ERC-721', _isGetFeeLimitNFT: boolean = false)
    {
        const provider = this.providerFactoryService.getProvider(_chainId);
        this.signer = new ethers.VoidSigner(this.address, provider);

        let contractPopulatedTx;

        if (_nftType == 'ERC-721')
        {
            const { contract } = await this.contractFactory.create(_contractAddress, _chainId, ERC721_METADATA_ABI);

            contractPopulatedTx = await contract.transferFrom.populateTransaction(
                this.address, _data.to, _data.tokenId
            );
        }
        else
        {
            const { contract } = await this.contractFactory.create(_contractAddress, _chainId, ERC1155_METADATA_ABI);
            contractPopulatedTx = await contract.safeTransferFrom.populateTransaction(
                this.address, _data.to, _data.tokenId, 1, '0x'
            );
        }


        const gas = await this.signer.estimateGas(contractPopulatedTx);

        contractPopulatedTx[ 'gasLimit' ] = gas;

        let tx;
        try
        {
            tx = await this.signer.populateTransaction(contractPopulatedTx);
        } catch (e)
        {
            console.error(e);
            throw e;
        }

        if (_isGetFeeLimitNFT)
        {
            return (Number(ethers.formatUnits(tx.gasLimit, 'wei')) * 1.3).toString();

        }

        return tx;
    }

    public async sendNFTTransaction(
        _tx: ethers.TransactionRequest,
        _txInfo: { symbol: string, value: string, to?: string; },
        _chainId: number,
        _feeRequired: boolean = true
    ): Promise<any>
    {
        let txResponse;

        if (!_feeRequired)
        {
            delete _tx.maxFeePerGas;
            delete _tx.maxPriorityFeePerGas;
            txResponse = await this.sendTransactionOld(_tx, _txInfo, _chainId);

            return txResponse;
        }
        try
        {
            const txInfo = { ..._txInfo };

            delete _tx.gasPrice;

            txResponse = await this.sendTransaction(_tx, txInfo, _chainId);

            return txResponse;
        } catch (error)
        {
            console.error(error);
            throw error;
        }
    }

    public async getTransactionsCount(_chainId: number, address?: string): Promise<number>
    {
        try
        {
            const provider = this.providerFactoryService.getProvider(_chainId);
            const signer = new ethers.VoidSigner(address || this.address, provider);
            return await signer.getNonce('pending');

        } catch (error)
        {
            return null;
        }
    }

    public async checkContractAddress(_chainId: number, address: string): Promise<boolean>
    {
        const provider = this.providerFactoryService.getProvider(_chainId);

        try
        {
            const code = await provider.getCode(address);
            const isContract = code !== '0x';
            return isContract;

        } catch (error)
        {
            return false;
        }
    }

    private async initHidSettings()
    {
        const hidSettingKeys = Object.keys(this.HID_CUSTOM_CONFIG)
            .filter(key =>
            {
                const objectKey = key.toLowerCase();
                const name = this.name.toLowerCase();
                return name.includes(objectKey) || objectKey.includes(name);
            });

        const chainsToBeEnabledSet = new Set();
        hidSettingKeys.forEach(key =>
        {
            const chains: number[] = Object.keys(this.HID_CUSTOM_CONFIG[ key ]).map(Number);
            chains.forEach(chain =>
            {
                chainsToBeEnabledSet.add(chain);
            });
        });

        const chainsToBeEnabled = [ ...chainsToBeEnabledSet ];

        await Promise.allSettled(chainsToBeEnabled.map(async (chainId: eChainId) =>
        {
            const network = await this.networksService.getNetworkByChainId(chainId);
            if (!network)
            {
                await this.networksService.setNetworkByChainId(chainId, true);
            }
        }));

        const tokensToBeEnabled = [];

        hidSettingKeys.forEach(key =>
        {
            const chainsConfig = this.HID_CUSTOM_CONFIG[ key ];
            if (!chainsConfig) return;
            chainsToBeEnabled.forEach(chain =>
            {
                const tokens = chainsConfig[ chain ];
                if (!tokens) return;
                tokens.forEach(token =>
                {
                    const tokenToAdd = {
                        chainId: chain,
                        address: token.toLowerCase(),
                    };

                    const hasToken = tokensToBeEnabled.find(t => t.chainId === tokenToAdd.chainId && t.address === tokenToAdd.address);

                    if (!hasToken)
                    {
                        tokensToBeEnabled.push(tokenToAdd);
                    }
                });
            });
        }
        );


        for (let token of tokensToBeEnabled)
        {
            const enabledToken = this.currencies.find(
                c => c.getAddress().toLowerCase() === this.address.toLowerCase()
                    && c.getChainId() === token.chainId
                    && c.getTokenAddress()?.toLowerCase() === token.address.toLowerCase()
            );

            if (!enabledToken)
            {
                const blockchain = await this.networksService.getBlockchainByChainId(token.chainId);
                const tokenFromApi = await this.tokenApi.getTokenByBlockchainIdAndAddress(blockchain.id, token.address);
                await this.tokenApi.setWalletToken(Number(this.id), tokenFromApi.id);
                await this.addCurrencyByContract(
                    token.address,
                    tokenFromApi.symbol,
                    tokenFromApi,
                    tokenFromApi.id
                );
            }
        }

        this.currenciesSubject.next(this.currencies);
        this.currenciesLoadedSubject.next(true);
    }
}
